import update from 'immutability-helper';
import axios from 'axios';
import Cookies from 'js-cookie';
import { captureException } from '@sentry/react';

import { UATHeader } from 'store/common/constants/header';
import { getCatalogProductPromotionId } from 'store/ProductCatalog/products/selectors';
import {
  getCurrentRegionCode,
  isFlocktoryEnabledSelector,
  getCurrentRegionId,
  selectIsBasketRedirectEnabled,
} from 'store/common/settings/selectors';
import { cookieBasketService, flocktoryService } from 'services';
import { redirectToBasket, composeDeleteBasketUrl } from 'utils/basket';
import { setQuantity } from 'store/common/specialProjectHeader/cart';

import {
  MAXIMUM_ITEMS_COUNT_ERROR_CODE,
  MAXIMUM_ITEMS_COUNT_ERROR_MESSAGE,
  ADD_BASKET_ITEM,
  SOURCES,
  SET_COMMON_LOADING,
  SET_BASKET_DATA,
} from './constants';
import {
  getBasketContents,
  getBasketContentCookieName,
  getBasketCreditCookieName,
  getBasketCookiePath,
  getBasketMaxQuantity,
  getMainCollectionId,
  getIsItemInBasket,
  selectIsPreorderItemInBasketAndExcluded,
  selectItemIdFromId,
} from './selectors';
import { equalOrBothMissing, mergeServices } from './helpers';
import { isUATStand } from '../ab/helpers';
import { cartApi } from '../../../api';

const setBasketContents =
  ({ items = [], fancynumbers = [], promoKey = null }) =>
  (dispatch, getState) => {
    const state = getState();

    const basketItems = items.map((basketItem) => ({
      id: parseInt(basketItem.id, 10),
      quantity: basketItem.quantity,
      hasCredit: basketItem.hasCredit,
      article: basketItem.article,
      uuid: basketItem.uuid,
      promoId: basketItem.promoId,
      addSimQuantity: basketItem.addSimQuantity,
      bundleTariffModels: 'test',
      services: basketItem.services,
      isPreorder: basketItem.isPreorder,
      existedSubscriber: basketItem.existedSubscriber,
      source: basketItem.source,
    }));

    const storage = (window || {}).localStorage;
    storage.setItem(
      getBasketContentCookieName(state),
      JSON.stringify({
        items: basketItems,
        fancynumbers,
        promo_key: promoKey,
      }),
    );
  };

/** set cookie for credit-name */
export const setCreditSlug = (slug) => (dispatch, getState) => {
  const state = getState();
  const path = getBasketCookiePath(state);

  Cookies.set(getBasketCreditCookieName(state), slug, { expires: 365, path });
};

/**
 * Remake of `QAScripts/Pages/Header/QA.Beeline.ShopBasket/addItem`
 * @param id - product id
 * @param quantity - product count increment
 * @param promoId - product bundle id (promotion.id)
 * @param hasCredit
 * @param services
 * @param errorCallback
 */
export const addBasketItem =
  ({
    isPreorder = false,
    productId,
    origin,
    quantity = 1,
    promoId = null,
    hasCredit = false,
    services = [],
    fancynumber = null,
    errorCallback,
    source = undefined,
    price,
    brand,
    category,
  }) =>
  (dispatch, getState) => {
    /** assert input */
    console.assert(typeof productId === 'number', 'Identifier must be a number');
    console.assert(typeof quantity === 'number', 'Quantity must be a number');
    console.assert(
      typeof promoId === 'number' || !promoId,
      'promoId must either be a string or falsy',
    );
    console.assert(typeof hasCredit === 'boolean', 'hasCredit must be a boolean');
    console.assert(Array.isArray(services), 'services must be an array');
    console.assert(
      typeof errorCallback === 'function' || typeof errorCallback === 'undefined',
      'errorCallback must be a function or not defined',
    );

    const state = getState();
    const isFlocktoryEnabled = isFlocktoryEnabledSelector(state);
    const maxQuantity = getBasketMaxQuantity(state);
    let basketContent = getBasketContents(getState());

    /** find index of item in basket matching requested */
    const basketItemIndex = basketContent.items.findIndex(
      (item) => item.id === productId && equalOrBothMissing(item.promoId, promoId),
    );

    /** if item does not exist, we add new one */
    let basketItem;
    if (basketItemIndex === -1) {
      basketItem = {
        id: productId,
        quantity: 0,
        promoId: promoId || null,
        hasCredit: null,
        services: [],
        isPreorder,
        source,
      };
    } else {
      basketItem = basketContent.items[basketItemIndex];
    }
    /** check against maximumQuantity */
    if (
      basketItem.quantity + quantity <= maxQuantity ||
      (basketItem.isPreorder && basketItem.quantity + quantity > 1)
    ) {
      basketItem = update(basketItem, {
        quantity:
          basketItem.isPreorder ?
            {
              $set: 1,
            }
          : { $apply: (value) => value + quantity },
        hasCredit: { $set: hasCredit },
        services: { $set: mergeServices(basketItem.services, services) },
      });

      /** if add new item to the start or replacing existing */
      basketContent = update(basketContent, {
        items:
          basketItemIndex === -1 ?
            {
              $unshift: [basketItem],
            }
          : {
              [basketItemIndex]: { $set: basketItem },
            },
      });

      /** add fancynumber */
      if (fancynumber) {
        if (!basketContent.fancynumbers) {
          basketContent.fancynumbers = [];
        }
        basketContent.fancynumbers.push(fancynumber);
      }

      /** add origin */

      if (origin) {
        basketContent.origin = origin;
      }

      dispatch(setBasketContents(basketContent));
      dispatch({ type: ADD_BASKET_ITEM, rnd: Math.random() });
      const currentRegion = getCurrentRegionId(state);

      const flocktoryParams = {
        id: `${currentRegion}_${productId}`,
        price,
        count: 1,
        brand,
        categoryId: category,
      };
      const isValidItem = Object.values(flocktoryParams).every((value) => value !== undefined);

      if (isFlocktoryEnabled && isValidItem) {
        flocktoryService.addToCart(flocktoryParams);
      }

      return true;
    }
    if (errorCallback) {
      errorCallback({
        message: MAXIMUM_ITEMS_COUNT_ERROR_MESSAGE,
        code: MAXIMUM_ITEMS_COUNT_ERROR_CODE,
        max: maxQuantity,
      });
    }
    return false;
  };

export const addBasketTariff =
  ({ fancynumber = null }) =>
  (dispatch, getState) => {
    const state = getState();
    const basketContent = getBasketContents(state);

    if (!basketContent.fancynumbers) {
      basketContent.fancynumbers = [];
    }
    basketContent.fancynumbers.push(fancynumber);

    dispatch(setBasketContents(basketContent));
    dispatch({ type: ADD_BASKET_ITEM, rnd: Math.random() });
  };

export const updateCartData = (items) => (dispatch) => {
  dispatch({
    type: SET_BASKET_DATA,
    payload: items,
  });
};

/**
 * TODO: remove isInBasket from params
 * method for adding CrossSellProduct to basket,
 * if item is already added, moves to basket instead
 */
export const addProductToBasket =
  ({
    isInBasket,
    productId,
    forceRedirect,
    fancynumber = null,
    isTariff = false,
    price = undefined,
    brand = undefined,
    source = undefined,
    categoryId = undefined,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const mainCategoryId = getMainCollectionId(state);
    const promoId = getCatalogProductPromotionId(state)[productId];
    const regionCode = getCurrentRegionCode(state);
    const isBasketRedirectEnabled = selectIsBasketRedirectEnabled(state);
    const { isFlocktoryEnabled } = state.settings;
    const newBasketId = cookieBasketService.getBasketIdCookie();
    const itemId = selectItemIdFromId(state, productId?.toString());
    const isItemInBasket = getIsItemInBasket(state, productId?.toString());
    const isPreorderItemInBasketAndExcluded = selectIsPreorderItemInBasketAndExcluded(
      state,
      productId?.toString(),
    );

    let removeUrl = null;
    if (newBasketId && itemId) {
      removeUrl = composeDeleteBasketUrl(newBasketId, itemId);
    }

    if (isItemInBasket) {
      window.location = '/basket';
    } else {
      const currentRegion = getCurrentRegionId(state);
      const flocktoryParams = {
        id: `${currentRegion}_${productId}`,
        price,
        count: 1,
        brand,
        categoryId: categoryId || mainCategoryId,
      };

      if (isFlocktoryEnabled) {
        flocktoryService.addToCart(flocktoryParams);
      }

      if (!isInBasket) {
        if (isTariff) {
          dispatch(
            addBasketTariff({
              fancynumber,
            }),
          );
        } else {
          dispatch(
            addBasketItem({
              productId,
              promoId,
              fancynumber,
              source: SOURCES[source],
            }),
          );
        }
        if (!forceRedirect) {
          /* return true */
        }
      }

      // запустить лоадер
      dispatch({
        type: SET_COMMON_LOADING,
        payload: true,
      });

      // Если есть исключенный предзаказ, удалем полностью и добавляем заново, чтобы
      // можно было добавить только 1 раз
      if (isPreorderItemInBasketAndExcluded && removeUrl) {
        try {
          await removePreoderItem(removeUrl);
        } catch (error) {
          captureException(error);
        }
      }

      // если что-то идет не так уводим на старую корзину
      const onError = () => {
        cookieBasketService.setBasketIdCookie('');
        dispatch({
          type: SET_COMMON_LOADING,
          payload: false,
        });
      };

      const newBasketItem = {
        itemType: 'product',
        id: productId,
        quantity: 1,
        isExcluded: false,
        parentItemId: null,
        details: { promotionId: promoId },
        source: SOURCES[source],
      };

      if (newBasketId) {
        const onSuccess = (data) => {
          if (data) {
            dispatch(setQuantity(data));

            dispatch(updateCartData(data?.items || []));

            dispatch({
              type: SET_COMMON_LOADING,
              payload: false,
            });

            if (isBasketRedirectEnabled) {
              redirectToBasket();
            }
          }
        };

        await cartApi.basketMergeItems(newBasketId, [newBasketItem]).then(onSuccess).catch(onError);
      } else {
        const onSuccess = (data) => {
          if (data) {
            dispatch(setQuantity(data));
            dispatch(updateCartData(data?.items || []));

            dispatch({
              type: SET_COMMON_LOADING,
              payload: false,
            });

            cookieBasketService.setBasketIdCookie(data.id);

            if (isBasketRedirectEnabled) {
              redirectToBasket();
            }
          }
        };

        await cartApi
          .basketCreateBasket(cartApi.helpers.createBasketData([newBasketItem], regionCode))
          .then(onSuccess)
          .catch(onError);
      }
    }

    return false;
  };

export const addToBasketRequest = (data, url, onSuccess, onError, isUAT) => {
  const headers = {
    // 'X-Requested-With': 'XMLHttpRequest',
  };

  if (isUAT) {
    headers['x-api-302eccb3-6d67-498e-8c02-3483f0e6990b'] = true;
  }

  axios({
    method: 'post',
    url,
    data,
    responseType: 'json',
    headers,
    withCredentials: true,
  })
    .then((response) => {
      onSuccess(response);
    })
    .catch((e) => {
      onError();
      captureException(e);
    });
};

export const removePreoderItem = (url) => {
  const headers = {
    // 'X-Requested-With': 'XMLHttpRequest',
  };

  const isUAT = isUATStand();

  if (isUAT) {
    headers[UATHeader] = true;
  }

  return axios({
    method: 'delete',
    url,
    responseType: 'json',
    headers,
    withCredentials: true,
  });
};

// Получение списка товаров в корзине
export const getBasketData = () => (dispatch) => {
  const basketId = cookieBasketService.getBasketIdCookie();
  if (basketId) {
    cartApi
      .basketGetBasket(
        basketId,
        {},
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
      )
      .then((data) => {
        dispatch(setQuantity(data));
        dispatch({
          type: SET_BASKET_DATA,
          payload: data.items || [],
        });
      })
      .catch((error) => {
        captureException(error);
      });
  } else {
    dispatch({
      type: SET_BASKET_DATA,
      payload: [],
    });
  }
};
